// BeanRig (§24.2, §12.4, §11). WP5. One bean: a lathe body with a patterned toon skin and a face
// decal (6 expressions), rattling googly eyes, noodle arms on Verlet chains, waddling feet,
// volume-preserving squash & stretch, leans, the identity ring + glyph badge, the local player's
// pulsing ring and chevron, team ring, Dizzy stars / sweat / flush / spiral eyes, the ghost look,
// and every attachment (hot potato, balloon, parachute umbrella, crowns, boxing glove). The whole
// character is one skinned mesh + one hull (attachments add a pair only while shown), and
// `update()` allocates nothing.
import { Group, Matrix4, Quaternion, Vector3, type Object3D, type SkinnedMesh } from 'three';
import { NO_SLOT, TUNE } from '@shared/constants';
import {
  catalogItem, COLORBLIND_IDENTITY_HEX, DEFAULT_COSMETICS, IDENTITIES, TEAM_STYLES, type Cosmetics,
} from '@shared/cosmetics';
import { EV, type SimEvent } from '@shared/events';
import { BS } from '@shared/ids';
import { angleDiff, wrapAngle } from '@shared/math';
import type { Vec2 } from '@shared/types';
import type { BeanView, FrameView } from '../../game/FrameView';
import { placeAt, yawForFacing } from '../../core/space';
import { patternSecondary } from '../materials/colors';
import { OUTLINE_WIDTH, libraryShared, type MaterialLibrary } from '../materials/library';
import { createPartMaterials, partDepthMaterial, partHullMaterial, type PartMaterials } from '../materials/partMaterial';
import { PartSkin } from '../materials/partSkin';
import { TIERS } from '../quality';
import { approach, chooseExpression, clamp, createExprTimers, dizzyStars, len2, len3, Pupil, smooth01, Spring } from './anim';
import type { HatInfo } from './hats';
import { EXPR, mouthAtlas, type Expr } from './mouthAtlas';
import {
  ATTACHMENTS, attachmentGeometry, BODY_HEIGHT, BONE_ARM_L, BONE_ARM_R, BONE_COUNT, BONE_STRING, CHAIN_RINGS,
  coreGeometry, EYE_R, eyeStyle, FOOT, GLYPH_BADGE, HEAD_ANCHOR_Y, P, PUPIL_R, RING, type AttachmentId,
  type EyeStyle,
} from './rigGeometry';
import { VerletChain } from './VerletChain';

export interface BeanRig {
  readonly root: Object3D;
  setLook(c: Cosmetics, identity: number, team: number, isLocal: boolean): void;
  update(b: BeanView, view: FrameView, hitstopMs: number): void;   // pose + animation + expression; holds pose while hitstopMs > 0
  impulse(kind: 'hit' | 'land' | 'lunge' | 'grow' | 'squash', strength: number, dir?: Vec2): void;
  headWorldPos(out: Vector3): Vector3;
  dispose(): void;
  /**
   * Additive (WP5): sim events, for the cues a BeanView cannot carry — honks and emotes, the smug
   * face after a credited KO (§12.4), hit reactions, grab/throw squashes, the pebble cooldown. The
   * Renderer forwards every sim event to every rig; each rig picks out its own slot.
   */
  onSim?(ev: SimEvent, view: FrameView): void;
  /** Additive (WP5): the §12.2 colorblind identity palette for rings, glyphs, balloons and umbrellas. */
  setColorblind?(on: boolean): void;
}

/** §12.4 squash & stretch spring (stiffness 400, damping 18). */
const SQUASH = { k: 400, c: 18 } as const;
const TURN_TAU = 0.06;
const LEAN = { perAccel: 0.012, max: (18 * Math.PI) / 180, tau: 0.05 } as const;
const BOB = { idle: 0.02, idleHz: 1.2, run: 0.035 } as const;
const STEP_LEN = 0.45;
const FOOT_LIFT = 0.06;
const SHOULDER = { x: 0.4, y: 0.55, z: 0 } as const;
const ARM = { points: 6, seg: 0.1, damping: 0.96, gravity: -9.8 } as const;
const PULSE_HZ = 1;
const TEETER_HZ = 4;
const CHEVRON_ABOVE = 0.5;
const SMUG_SEC = 2;
const OUCH_SEC = 0.35;
const HONK_SEC = 0.35;
const EMOTE_SEC = 1.8;
const FLASH_MS = 33;
const CATCHUP_SEC = 0.06;
const EXPR_BLEND_SEC = 0.07;
const GHOST_SATURATION = 0.4;
const PEBBLE_COOLDOWN_SEC = TUNE.ghost.pebbleCooldownTicks / 60;
const GLOVE_REACH = TUNE.glove.reach;
const STAR_ORBIT = { r: 0.33, y: 0.12, speed: 2.8 } as const;

const IMPULSE_KIND = { hit: 0, land: 1, lunge: 2, grow: 3, squash: 4 } as const;

// Scratch (module-level: rigs update one at a time).
const mA = new Matrix4();
const mB2 = new Matrix4();
const mC = new Matrix4();
const mR = new Matrix4();
const mS = new Matrix4();
const qA = new Quaternion();
const qB = new Quaternion();
const vA = new Vector3();
const vB = new Vector3();
const AXIS_Z = new Vector3(0, 0, 1);

interface Attachment { mesh: SkinnedMesh; hull: SkinnedMesh }

/** Desaturates a (linear) color in place to the §12.4 ghost saturation. */
function desaturate(c: { r: number; g: number; b: number }, k: number): void {
  const lum = c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722;
  c.r = lum + (c.r - lum) * k;
  c.g = lum + (c.g - lum) * k;
  c.b = lum + (c.b - lum) * k;
}

export interface BeanRigDebug {
  expr: Expr;
  hand: [Vector3, Vector3];
  pupils: [Pupil, Pupil];
  flash: number;
  attachments: Record<AttachmentId, boolean>;
  visibleMeshes: number;
}

export class BeanRigImpl implements BeanRig {
  readonly root = new Group();
  private readonly lib: MaterialLibrary;
  private readonly skin = new PartSkin(BONE_COUNT);
  private readonly pm: PartMaterials;
  private readonly core: SkinnedMesh;
  private readonly coreHull: SkinnedMesh;
  private readonly aura: SkinnedMesh;
  private readonly attach: Record<AttachmentId, Attachment>;
  private segments = 0;
  private hatId = -1;
  private eyesId = -1;
  private hat: HatInfo = { top: 0.05, anim: 'none', pivot: [0, 0, 0] };
  private eye: EyeStyle = eyeStyle(0);
  private identity = 0;
  private team = 0;
  private isLocal = false;
  private colorblind = false;
  private look: Cosmetics = { ...DEFAULT_COSMETICS };
  private slot = NO_SLOT;

  // Animation state.
  private fresh = true;
  private time = 0;
  private frozen = false;
  private catchT = 0;
  private readonly catchOff = new Vector3();
  private readonly lastPos = new Vector3();
  private facing = 0;
  private spinYaw = 0;
  private tumble = 0;
  private walkPhase = 0;
  private hatSpin = 0;
  private prevVx = 0;
  private prevVy = 0;
  private prevVz = 0;
  private prevState: number = BS.NORMAL;
  private prevDizzy = 0;
  private airborne = false;
  private airTime = 0;
  /** Smoothed root-local acceleration (x right, y up, z forward). */
  private readonly acc = new Vector3();
  private readonly accLean = new Vector3();
  private readonly squash = new Spring(1, SQUASH.k, SQUASH.c);
  private readonly stretch = new Spring(1, SQUASH.k, SQUASH.c);
  private stretchX = 0;
  private stretchZ = 1;
  private readonly size = new Spring(1, 300, 13);
  private readonly hatJiggle = new Spring(0, 160, 7);
  private readonly inflate = new Spring(0, 220, 9);
  private readonly puff = new Spring(1, 260, 10);
  private readonly pupilL = new Pupil();
  private readonly pupilR = new Pupil();
  private readonly armL = new VerletChain(ARM.points, ARM.seg, ARM.damping, ARM.gravity);
  private readonly armR = new VerletChain(ARM.points, ARM.seg, ARM.damping, ARM.gravity);
  private readonly string = new VerletChain(8, 0.165, 0.94, -4);
  private readonly exprT = createExprTimers();
  private exprCur: Expr = EXPR.SMILE;
  private exprPrev: Expr = EXPR.SMILE;
  private exprBlend = 1;
  private flashUntil = -1;
  private blinkAt = 2;
  private blinkT = -1;
  private emote = 0;
  private emoteUntil = -1;
  private pebbleReadyAt = -1;
  private lastLandAt = -10;
  private lastLungeAt = -10;
  private lastHitAt = -10;
  private floatWas = false;
  private handsFlatUntil = -1;
  private readonly bodyM = new Matrix4();
  private readonly headM = new Matrix4();
  /** Top of the head or hat, root-local. */
  private readonly headTop = new Vector3(0, BODY_HEIGHT, 0);
  private readonly debugState: BeanRigDebug;

  constructor(lib: MaterialLibrary) {
    this.lib = lib;
    this.root.name = 'bean';
    this.pm = createPartMaterials(lib, mouthAtlas());
    const hullMat = partHullMaterial(lib, OUTLINE_WIDTH.bean);
    const depth = partDepthMaterial(lib);
    const seg = TIERS[lib.quality].beanSegments;
    const g = coreGeometry(seg, DEFAULT_COSMETICS.hat, DEFAULT_COSMETICS.eyes);
    this.core = this.skin.mesh(g.geometry, this.pm.main, 'beanBody');
    this.core.castShadow = true;
    this.core.receiveShadow = true;
    this.core.customDepthMaterial = depth;
    this.coreHull = this.skin.mesh(g.hull, hullMat, 'outline');
    this.aura = this.skin.mesh(g.hull, this.pm.aura, 'ghostAura');
    this.aura.visible = false;
    this.aura.renderOrder = 2;
    this.root.add(this.core, this.coreHull, this.aura);
    const attach = {} as Record<AttachmentId, Attachment>;
    for (const id of ATTACHMENTS) {
      const ag = attachmentGeometry(id, seg);
      const mesh = this.skin.mesh(ag.geometry, this.pm.main, `bean:${id}`);
      mesh.castShadow = true;
      mesh.customDepthMaterial = depth;
      const hull = this.skin.mesh(ag.hull, hullMat, 'outline');
      mesh.visible = false;
      hull.visible = false;
      this.root.add(mesh, hull);
      attach[id] = { mesh, hull };
    }
    this.attach = attach;
    this.pm.u.seed.value = Math.random() * 10;
    this.blinkAt = 1 + Math.random() * 3;
    for (let i = 0; i < BONE_COUNT; i++) this.skin.hide(i);
    this.debugState = {
      expr: EXPR.SMILE, hand: [new Vector3(), new Vector3()], pupils: [this.pupilL, this.pupilR], flash: 0,
      attachments: { potato: false, balloon: false, parachute: false, crowns: false, glove: false }, visibleMeshes: 0,
    };
    this.setLook(DEFAULT_COSMETICS, 0, 0, false);
  }

  // ---- Look --------------------------------------------------------------------------------

  setLook(c: Cosmetics, identity: number, team: number, isLocal: boolean): void {
    this.look = { ...c };
    this.identity = ((identity % IDENTITIES.length) + IDENTITIES.length) % IDENTITIES.length;
    this.team = team;
    this.isLocal = isLocal;
    const u = this.pm.u;
    const bodyHex = catalogItem('color', c.color)?.hex ?? IDENTITIES[this.identity].hex;
    u.body.value.set(bodyHex);
    patternSecondary(u.body2.value, bodyHex, c.pattern);
    u.pattern.value = c.pattern;
    u.glyph.value = this.identity;
    this.applyIdentityColor();
    if (team === 1 || team === 2) u.team.value.set(TEAM_STYLES[team].hex);
    this.applyGeometry();
  }

  setColorblind(on: boolean): void {
    if (on === this.colorblind) return;
    this.colorblind = on;
    this.applyIdentityColor();
  }

  private applyIdentityColor(): void {
    const u = this.pm.u;
    const hex = this.colorblind ? COLORBLIND_IDENTITY_HEX[this.identity] : IDENTITIES[this.identity].hex;
    u.ident.value.set(hex);
    u.ghostColor.value.set(hex);
    desaturate(u.ghostColor.value, GHOST_SATURATION);
  }

  /** (Re)binds the core and attachment geometry for the current look and quality tier. */
  private applyGeometry(): void {
    const seg = TIERS[this.lib.quality].beanSegments;
    if (seg === this.segments && this.look.hat === this.hatId && this.look.eyes === this.eyesId) return;
    const g = coreGeometry(seg, this.look.hat, this.look.eyes);
    this.core.geometry = g.geometry;
    this.coreHull.geometry = g.hull;
    this.aura.geometry = g.hull;
    if (seg !== this.segments) {
      for (const id of ATTACHMENTS) {
        const ag = attachmentGeometry(id, seg);
        this.attach[id].mesh.geometry = ag.geometry;
        this.attach[id].hull.geometry = ag.hull;
      }
    }
    this.segments = seg;
    this.hatId = this.look.hat;
    this.eyesId = this.look.eyes;
    this.hat = g.hat;
    this.eye = eyeStyle(this.look.eyes);
    const lim = (EYE_R - PUPIL_R * this.eye.pupil) * 0.92;
    this.pupilL.limit = lim;
    this.pupilR.limit = lim;
  }

  // ---- Events ------------------------------------------------------------------------------

  impulse(kind: 'hit' | 'land' | 'lunge' | 'grow' | 'squash', strength: number, dir?: Vec2): void {
    const s = Math.max(0, strength);
    switch (IMPULSE_KIND[kind]) {
      case IMPULSE_KIND.hit:
        this.hitReact(Math.max(2, s * 4), dir ? dir.x : 0, dir ? dir.y : 0, true);
        break;
      case IMPULSE_KIND.land:
        if (this.time - this.lastLandAt > 0.15) this.land(clamp(s * 8, 2, 12));
        break;
      case IMPULSE_KIND.lunge:
        if (this.time - this.lastLungeAt > 0.15) this.lunge();
        break;
      case IMPULSE_KIND.grow:
        this.size.v += clamp(s, 0, 4) * 1.5;
        this.squash.v -= 4;
        break;
      case IMPULSE_KIND.squash:
        this.squash.v -= clamp(s, 0, 4) * 4;
        break;
    }
  }

  onSim(ev: SimEvent, view: FrameView): void {
    const me = this.slot;
    if (me === NO_SLOT) return;
    switch (ev.type) {
      case EV.HIT:
        if (ev.b === me) {
          const b = view.beans[me];
          this.hitReact(ev.d / 10, b ? b.x - ev.x : 0, b ? b.y - ev.y : 0, false);
        } else if (ev.a === me) {
          // §11.1: both beans flash white for a frame; the attacker squashes into the hit.
          this.flashUntil = performance.now() + FLASH_MS;
          this.squash.v -= 3;
        }
        break;
      case EV.CLASH:
        if (ev.a === me || ev.b === me) {
          this.kickStretch(-0.9);
          this.pupilL.kick(0, 0.5);
          this.pupilR.kick(0, 0.5);
        }
        break;
      case EV.KO:
        // §12.4: smug for 2 s after a credited KO (flags: 4 self-fall, 32 teamkill).
        if (ev.b === me && ev.a !== me && (ev.d & (4 | 32)) === 0) this.exprT.smugUntil = this.time + SMUG_SEC;
        break;
      case EV.HONK:
        if (ev.a === me) {
          this.exprT.honkUntil = this.time + HONK_SEC;
          this.squash.v -= 5;
          this.puff.v += 3;
        }
        break;
      case EV.EMOTE:
        if (ev.a === me && ev.b >= 1 && ev.b <= 4) {
          this.emote = ev.b;
          this.emoteUntil = this.time + EMOTE_SEC;
          this.squash.v += 3;
        }
        break;
      case EV.GRAB:
        if (ev.a === me) this.squash.v -= 4;
        else if (ev.b === me) this.hitReact(2, 0, 0, false);
        break;
      case EV.THROW:
        if (ev.a === me) this.kickStretch(1.2);
        break;
      case EV.ESCAPE:
        if (ev.a === me || ev.b === me) this.squash.v += 5;
        break;
      case EV.STOMP:
        // §11.2 hand stomp: the hanger's hands flatten; the stomper squashes.
        if (ev.b === me) this.handsFlatUntil = this.time + 0.3;
        if (ev.b === me || ev.a === me) this.squash.v -= 6;
        break;
      case EV.TEETER_SAVE:
        if (ev.a === me) {
          this.squash.v += 6;
          this.puff.v += ev.c === 1 ? 4 : 2;
        }
        break;
      case EV.PEBBLE_DROP:
        if (ev.a === me) this.pebbleReadyAt = this.time + PEBBLE_COOLDOWN_SEC;
        break;
      case EV.SHAKE_START:
        if (ev.a === me) this.impulse('grow', 3);
        break;
      case EV.CROWN_PICK:
      case EV.ITEM_GET:
        if (ev.a === me) this.puff.v += 3;
        break;
      default:
        break;
    }
  }

  private hitReact(kb: number, dx: number, dy: number, fromImpulse: boolean): void {
    // Several sources report the same hit (impulse, the HIT event, the Dizzy jump): react once.
    if (this.time - this.lastHitAt < 0.05) return;
    this.lastHitAt = this.time;
    const k = clamp(kb, 0.5, 16);
    this.flashUntil = performance.now() + FLASH_MS;
    this.exprT.ouchUntil = this.time + OUCH_SEC;
    // §11.1: victim stretches along the knockback; §12.4: pupil impulse 3 × kb.
    const len = len2(dx, dy);
    if (len > 1e-4) this.setStretchDirWorld(dx / len, dy / len);
    this.kickStretch(clamp(0.6 + k * 0.4, 1, 6));
    // §12.4 "impulse 3 × kb": eye-plane speed 3·kb·0.12 m/s, so a plain 8 m/s shove (2.9 m/s)
    // slams the pupil into the rim and it rattles back and forth a few times.
    const kick = 3 * k * 0.12;
    const side = len > 1e-4 ? this.localX(dx / len, dy / len) : 0.6;
    this.pupilL.kick(side * kick, kick * 0.7);
    this.pupilR.kick(side * kick * 0.8, kick * 0.9);
    if (!fromImpulse) this.squash.v -= 2;
  }

  private land(speed: number): void {
    this.lastLandAt = this.time;
    // §11.1: squash 0.75 on land, scaled by impact.
    this.squash.v -= clamp(speed * 1.25, 2.5, 13);
    this.pupilL.kick(0, -speed * 0.04);
    this.pupilR.kick(0, -speed * 0.04);
  }

  private lunge(): void {
    this.lastLungeAt = this.time;
    // §11.1: squash 0.8 / stretch 1.2 along the lunge for ~90 ms.
    this.stretchX = 0;
    this.stretchZ = 1;
    this.kickStretch(8.5);
  }

  private kickStretch(v: number): void {
    this.stretch.v += v;
  }

  /** Root-local x of a sim-plane direction. */
  private localX(dx: number, dy: number): number {
    const yaw = yawForFacing(this.facing);
    return dx * Math.cos(yaw) - dy * Math.sin(yaw);
  }

  private setStretchDirWorld(dx: number, dy: number): void {
    const yaw = yawForFacing(this.facing);
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    this.stretchX = dx * c - dy * s;
    this.stretchZ = dx * s + dy * c;
  }

  // ---- Update --------------------------------------------------------------------------------

  update(b: BeanView, view: FrameView, hitstopMs: number): void {
    const killZ = TUNE.edge.killZ;
    const visible = b.active && (b.ghost ? b.z > killZ + 0.05 : !b.out && b.z > killZ + 0.01);
    this.root.visible = visible;
    if (!visible) {
      this.fresh = true;
      return;
    }
    this.slot = b.slot;
    if (TIERS[this.lib.quality].beanSegments !== this.segments) this.applyGeometry();
    const u = this.pm.u;
    u.flash.value = performance.now() < this.flashUntil ? 1 : 0;
    if (hitstopMs > 0 && !this.fresh) {
      // §2.13: hold the pose; the one-frame white flash still shows.
      this.frozen = true;
      return;
    }
    let dt = clamp(view.dt, 0, 0.1);
    const jump = len3(b.x - this.lastPos.x, b.y - this.lastPos.y, b.z - this.lastPos.z);
    if (this.fresh || jump > TUNE.lagcomp.teleportDist) {
      this.resetPose(b);
      dt = 0;
    } else if (this.frozen) {
      // §2.13: catch the frozen pose up to the true position over 60 ms.
      const p = this.root.position;
      this.catchOff.set(p.x - b.x, p.y - b.z, p.z - b.y);
      this.catchT = CATCHUP_SEC;
    }
    this.frozen = false;
    this.time += dt;
    libraryShared(this.lib).time.value = view.now / 1000;

    this.trackMotion(b, dt);
    this.poseRoot(b, dt);
    this.poseBody(b, dt);
    this.poseFace(b, dt);
    this.poseFeet(b);
    this.poseArms(b, dt);
    this.poseDecor(b);
    this.poseAttachments(b, view, dt);
    this.lastPos.set(b.x, b.y, b.z);
    this.prevState = b.state;
    this.prevDizzy = b.dizzy;
  }

  private resetPose(b: BeanView): void {
    this.fresh = false;
    this.facing = b.facing;
    this.spinYaw = 0;
    this.tumble = 0;
    this.prevVx = b.vx;
    this.prevVy = b.vy;
    this.prevVz = b.vz;
    this.prevState = b.state;
    this.prevDizzy = b.dizzy;
    this.airborne = b.z > TUNE.bean.airborneZ + 0.02;
    this.acc.set(0, 0, 0);
    this.accLean.set(0, 0, 0);
    this.squash.reset(1);
    this.stretch.reset(1);
    this.size.reset(b.radius > 0 ? b.radius / TUNE.bean.radius : 1);
    this.hatJiggle.reset(0);
    this.puff.reset(1);
    this.pupilL.reset();
    this.pupilR.reset();
    this.catchT = 0;
    this.floatWas = b.floating;
    this.inflate.reset(b.floating ? 1 : 0);
    const s = SHOULDER;
    this.armL.reset(-s.x, s.y, s.z, -0.15, -1, 0.05);
    this.armR.reset(s.x, s.y, s.z, 0.15, -1, 0.05);
    this.string.reset(0.35, 1.0, 0.1, -0.05, 1, -0.15);
  }

  /** Smoothed acceleration, plus takeoff / landing / lunge / hit inference from the view. */
  private trackMotion(b: BeanView, dt: number): void {
    if (dt <= 0) return;
    const ax = (b.vx - this.prevVx) / dt;
    const ay = (b.vy - this.prevVy) / dt;
    const az = (b.vz - this.prevVz) / dt;
    this.prevVx = b.vx;
    this.prevVy = b.vy;
    // Root-local (x right, z forward); vertical acceleration excludes gravity's steady pull.
    const yaw = yawForFacing(this.facing);
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    const lx = clamp(ax * c - ay * s, -80, 80);
    const lz = clamp(ax * s + ay * c, -80, 80);
    const ly = clamp(az + (this.airborne ? TUNE.jump.gravity : 0), -80, 80);
    const k = approach(dt, 0.03);
    this.acc.x += (lx - this.acc.x) * k;
    this.acc.y += (ly - this.acc.y) * k;
    this.acc.z += (lz - this.acc.z) * k;
    const kl = approach(dt, LEAN.tau);
    this.accLean.x += (lx - this.accLean.x) * kl;
    this.accLean.z += (lz - this.accLean.z) * kl;

    const air = b.z > TUNE.bean.airborneZ + 0.02 && b.state !== BS.LEDGE && b.state !== BS.CLIMB && b.state !== BS.GHOST;
    if (air && !this.airborne && b.vz > 2) this.squash.v += clamp(b.vz * 0.9, 2, 7);   // takeoff stretch
    if (!air && this.airborne && this.airTime > 0.08 && this.time - this.lastLandAt > 0.15) this.land(Math.abs(this.prevVz));
    this.airTime = air ? this.airTime + dt : 0;
    this.airborne = air;
    this.prevVz = b.vz;
    if (b.state === BS.LUNGE && this.prevState !== BS.LUNGE && this.time - this.lastLungeAt > 0.15) this.lunge();
    if (b.dizzy >= this.prevDizzy + 5 && b.state !== BS.GHOST) this.hitReact(clamp(len2(b.vx, b.vy), 3, 15), b.vx, b.vy, false);
    if (b.floating && !this.floatWas) {
      this.inflate.reset(0.15);
      this.inflate.v = 6;
    }
    this.floatWas = b.floating;
  }

  private poseRoot(b: BeanView, dt: number): void {
    let target = b.facing;
    if (b.state === BS.LEDGE || b.state === BS.CLIMB) target = Math.atan2(b.edgeNy, b.edgeNx);
    this.facing = wrapAngle(this.facing + angleDiff(this.facing, target) * approach(dt, TURN_TAU));
    // Spins: a peel slip (§11.3: 3 rev/s) and the teacup (1.5 rev/s); unwind the short way after.
    if (b.state === BS.SLIP) this.spinYaw += dt * 3 * Math.PI * 2;
    else if (b.state === BS.SPUN) this.spinYaw += dt * 1.5 * Math.PI * 2;
    else {
      this.spinYaw = wrapAngle(this.spinYaw);
      this.spinYaw -= this.spinYaw * approach(dt, 0.1);
    }
    placeAt(this.root, b.x, b.y, b.z);
    if (this.catchT > 0) {
      this.root.position.addScaledVector(this.catchOff, this.catchT / CATCHUP_SEC);
      this.catchT = Math.max(0, this.catchT - dt);
    }
    this.root.rotation.set(0, yawForFacing(this.facing) + this.spinYaw, 0);
    // Big Shake (§3.3, §11.3): the visual size springs toward radius / 0.45 (grow over ~12 frames).
    this.size.target = b.radius > 0 ? b.radius / TUNE.bean.radius : 1;
    this.root.scale.setScalar(Math.max(0.2, this.size.step(dt)));
  }

  private poseBody(b: BeanView, dt: number): void {
    const t = this.time;
    const st = b.state;
    const speed = len2(b.vx, b.vy);
    const grounded = !this.airborne;
    const running = grounded && speed > 0.6 && (st === BS.NORMAL || st === BS.CARRYING || st === BS.GRAB_START || st === BS.PUNCH_WINDUP || st === BS.FROZEN);
    if (grounded && speed > 0.3) this.walkPhase += (speed * dt * Math.PI) / STEP_LEN;
    const emoting = this.emoting(b);

    // Bob: idle breathing 2 cm at 1.2 Hz (§12.4), a bounce per step when running, a ghost float.
    let bob = 0;
    if (b.ghost) bob = 0.08 * Math.sin(t * 2 * Math.PI * 0.7);
    else if (running) bob = BOB.run * Math.abs(Math.sin(this.walkPhase)) * clamp(speed / 4, 0.4, 1);
    else if (grounded) bob = BOB.idle * (0.5 + 0.5 * Math.sin(t * 2 * Math.PI * BOB.idleHz));

    // Lean toward acceleration (§12.4: accel × 0.012, ±18°), plus state tilts.
    let lx = clamp(this.accLean.x * LEAN.perAccel, -LEAN.max, LEAN.max);
    let lz = clamp(this.accLean.z * LEAN.perAccel, -LEAN.max, LEAN.max);
    if (b.ghost || st === BS.GRABBED || st === BS.LEDGE || st === BS.CLIMB || st === BS.FALLING) {
      lx = 0;
      lz = 0;
    }
    let tiltX = lz;   // about +x: tips the top toward +z (forward)
    let tiltZ = -lx;  // about +z: tips the top toward −x
    if (running) tiltZ += 0.07 * Math.sin(this.walkPhase);                 // waddle
    if (st === BS.TEETER) {
      // §11.2: lean out and back on a 4 Hz wobble, toward the void.
      const out = 0.22 + 0.16 * Math.sin(t * 2 * Math.PI * TEETER_HZ);
      const yaw = yawForFacing(this.facing);
      const ox = -(b.edgeNx * Math.cos(yaw) - b.edgeNy * Math.sin(yaw));
      const oz = -(b.edgeNx * Math.sin(yaw) + b.edgeNy * Math.cos(yaw));
      tiltX += oz * out;
      tiltZ -= ox * out;
    }
    if (st === BS.GRAB_MISS) tiltX += 0.26 * Math.sin(Math.min(1, b.st / TUNE.grab.missTicks) * Math.PI);   // §11.1 stumble 15°
    if (st === BS.CARRYING) tiltX -= 0.12;
    if (st === BS.GRABBED) {
      tiltZ += 0.22 * Math.sin(t * 17);
      tiltX += 0.12 * Math.sin(t * 13);
    }
    if (st === BS.PUNCH_WINDUP) tiltX -= 0.18;
    if (st === BS.PUNCH) tiltX += 0.2 * (1 - b.st / TUNE.glove.punchTicks);
    if (st === BS.TUMBLE && grounded) tiltX -= 0.3;
    if (b.floating) tiltZ += 0.08 * Math.sin(t * 2.1);
    if (st === BS.RESPAWN) tiltZ += 0.1 * Math.sin(t * 2.6);
    if (st === BS.FALLING) {
      // §11.2 fall: lean back so the screaming face looks up at the camera, flailing.
      tiltX -= 0.62 + 0.08 * Math.sin(t * 7);
      tiltZ += 0.2 * Math.sin(t * 5);
    }
    if (b.ghost) {
      tiltZ += 0.1 * Math.sin(t * 1.3);
      tiltX += 0.12;
    }
    this.puff.target = 1;
    if (emoting) {
      const k = (this.emoteUntil - this.time) / EMOTE_SEC;
      const env = smooth01(0, 0.15, k) * smooth01(1, 0.85, k);
      if (this.emote === 2) {                                               // dance
        bob += 0.07 * Math.abs(Math.sin(t * 2 * Math.PI * 2.2)) * env;
        tiltZ += 0.24 * Math.sin(t * 2 * Math.PI * 1.1) * env;
      } else if (this.emote === 3) {                                        // laugh
        tiltX -= 0.2 * env;
        this.squash.v += Math.sin(t * 2 * Math.PI * 7) * 2.2 * env;
      } else if (this.emote === 4) {                                        // flex
        this.puff.target = 1 + 0.07 * env;
      } else {                                                              // wave
        tiltZ += 0.08 * Math.sin(t * 2 * Math.PI * 1.6) * env;
      }
    }

    // Tumble spin about the horizontal axis (§11.1: thrown victims spin 1.5 rev/s while airborne).
    const flying = (st === BS.TUMBLE || st === BS.YANKED) && this.airborne;
    if (flying) this.tumble += dt * 1.5 * Math.PI * 2;
    else {
      this.tumble = wrapAngle(this.tumble);
      this.tumble -= this.tumble * approach(dt, 0.07);
    }
    let spAx = 1;
    let spAz = 0;
    if (speed > 0.5) {
      // Axis perpendicular to the horizontal velocity, so the bean rolls over its direction of travel.
      const yaw = yawForFacing(this.facing);
      const vxl = b.vx * Math.cos(yaw) - b.vy * Math.sin(yaw);
      const vzl = b.vx * Math.sin(yaw) + b.vy * Math.cos(yaw);
      spAx = vzl / speed;
      spAz = -vxl / speed;
    }

    // Squash & stretch (§12.4: volume preserving; spring 400/18).
    const s = clamp(this.squash.step(dt), 0.55, 1.6);
    if (st === BS.YANKED && speed > 1) {
      this.setStretchDirWorld(b.vx / speed, b.vy / speed);
      this.stretch.target = 1.25;
    } else this.stretch.target = 1;
    const q = clamp(this.stretch.step(dt), 0.62, 1.6);
    const puff = this.puff.step(dt);
    composeBody(this.bodyM, bob, tiltX, tiltZ, this.tumble, spAx, spAz, s, q, this.stretchX, this.stretchZ, puff);
    this.skin.set(P.BODY, this.bodyM);

    // Head anchor (§12.4: (0, 1.05, 0)); hats jiggle on squash velocity and lean.
    this.hatJiggle.target = 0;
    this.hatJiggle.v += (-this.squash.v * 0.02 - this.accLean.z * 0.0006) * Math.min(1, dt * 60);
    const jig = clamp(this.hatJiggle.step(dt), -0.35, 0.35);
    mA.makeTranslation(0, HEAD_ANCHOR_Y, 0);
    mR.makeRotationX(jig);
    const hs = this.hat.scale ?? 1;
    mS.makeScale(hs, hs, hs);
    this.headM.multiplyMatrices(this.bodyM, mA).multiply(mR).multiply(mS);
    this.skin.set(P.HEAD, this.headM);
    this.poseHatAnim(speed, dt);
    this.headTop.set(0, Math.max(0.05, this.hat.top), 0).applyMatrix4(this.headM);
  }

  private emoting(b: BeanView): boolean {
    return this.time < this.emoteUntil && !this.airborne && (b.state === BS.NORMAL || b.state === BS.FROZEN);
  }

  private poseHatAnim(speed: number, dt: number): void {
    const h = this.hat;
    const px = h.pivot[0];
    const py = h.pivot[1];
    const pz = h.pivot[2];
    const t = this.time;
    mA.makeTranslation(px, py, pz);
    switch (h.anim) {
      case 'spin':
        // Propeller: idles, races when running, flying or falling.
        this.hatSpin += (5 + speed * 2.2 + (this.airborne ? 14 : 0)) * dt;
        mR.makeRotationY(this.hatSpin);
        break;
      case 'bob':
        mR.makeRotationZ(0.08 * Math.sin(t * 1.7));
        mR.setPosition(0, 0.025 * Math.sin(t * 2.4), 0);
        break;
      case 'wiggle': {
        const twitch = Math.sin(t * 1.3) > 0.97 ? Math.sin(t * 40) * 0.25 : 0;
        mR.makeRotationZ(twitch + 0.04 * Math.sin(t * 3));
        break;
      }
      case 'swim':
        this.hatSpin += dt * 1.6;
        mR.makeRotationY(this.hatSpin);
        break;
      case 'jiggle':
        mR.makeRotationX(clamp(-this.squash.v * 0.03 + this.hatJiggle.x * 0.8, -0.4, 0.4));
        break;
      default:
        mR.identity();
    }
    mC.makeTranslation(-px, -py, -pz);
    mB2.multiplyMatrices(this.headM, mA).multiply(mR).multiply(mC);
    this.skin.set(P.HAT_ANIM, mB2);
  }

  private poseFace(b: BeanView, dt: number): void {
    const u = this.pm.u;
    const t = this.time;
    const e = this.eye;
    const spiral = b.dizzy >= TUNE.dizzy.spiralAt && !b.ghost;
    u.spiral.value = spiral ? 1 : 0;
    u.flush.value += ((b.dizzy >= TUNE.dizzy.flushAt && !b.ghost ? 1 : 0) - u.flush.value) * approach(dt, 0.25);
    u.blush.value = b.ghost ? 0 : 0.5 + 0.5 * clamp(b.dizzy / 100, 0, 1);

    // Expression (§12.4) with a short crossfade.
    const want = chooseExpression(b, this.exprT, this.time);
    if (want !== this.exprCur) {
      this.exprPrev = this.exprCur;
      this.exprCur = want;
      this.exprBlend = 0;
    }
    this.exprBlend = Math.min(1, this.exprBlend + (dt > 0 ? dt / EXPR_BLEND_SEC : 1));
    u.expr.value[0] = this.exprPrev;
    u.expr.value[1] = this.exprCur;
    u.expr.value[2] = this.exprBlend;

    // Blinks every 2.5–5.5 s.
    if (this.blinkT < 0 && t > this.blinkAt) this.blinkT = 0;
    let blink = 1;
    if (this.blinkT >= 0) {
      this.blinkT += dt;
      const k = this.blinkT / 0.13;
      blink = k < 1 ? 1 - 0.88 * Math.sin(k * Math.PI) : 1;
      if (k >= 1) {
        this.blinkT = -1;
        this.blinkAt = t + 2.5 + 3 * (0.5 + 0.5 * Math.sin(t * 12.9898 + this.identity * 7.1));
      }
    }
    const laughing = this.time < this.emoteUntil && this.emote === 3;
    if (laughing) blink = 0.35;
    const scared = b.state === BS.TEETER || b.state === BS.FALLING || b.state === BS.LEDGE;
    if (spiral || scared) blink = 1;
    const wide = scared ? 1.14 : 1;   // §11.2 teeter: eyes wide

    // Pupils lag the head's acceleration and rest a little up and in: the beans look up at the
    // (high) gameplay camera, so faces read from above.
    const restY = e.scale * 0.016;
    const restX = clamp(this.acc.x * 0.0004, -0.02, 0.02);
    const ay = this.acc.y + this.acc.z * 0.4;
    this.pupilL.step(dt, this.acc.x, ay, restX, restY);
    this.pupilR.step(dt, this.acc.x, ay, restX, restY);

    for (let i = 0; i < 2; i++) {
      const side = i === 0 ? -1 : 1;
      const pupil = i === 0 ? this.pupilL : this.pupilR;
      const es = e.scale * wide;
      mA.makeTranslation(side * e.x, e.y, e.z);
      mS.makeScale(es, es * blink, es);
      mB2.multiplyMatrices(this.bodyM, mA).multiply(mS);
      this.skin.set(i === 0 ? P.EYE_L : P.EYE_R, mB2);
      // Lids, brows or lashes ride the eye (sleepy lids droop further and close on blinks).
      if (e.lids) {
        const droop = laughing ? 0.9 : 0.12 + (1 - blink) * 1.3 + (b.dizzy >= TUNE.dizzy.flushAt ? 0.25 : 0);
        mR.makeRotationX(droop);
        mC.multiplyMatrices(mB2, mR);
        this.skin.set(i === 0 ? P.LID_L : P.LID_R, mC);
      } else if (e.brows) {
        mR.makeTranslation(0, scared || this.exprCur === EXPR.O ? 0.035 : 0, 0);
        mC.multiplyMatrices(mB2, mR);
        this.skin.set(i === 0 ? P.LID_L : P.LID_R, mC);
      } else if (e.lashes) this.skin.set(i === 0 ? P.LID_L : P.LID_R, mB2);
      else this.skin.hide(i === 0 ? P.LID_L : P.LID_R);
      // The pupil on the eye's front surface, tilted to the sphere's normal at its spot.
      if (spiral || blink < 0.3) {
        this.skin.hide(i === 0 ? P.PUPIL_L : P.PUPIL_R);
        continue;
      }
      const px = pupil.x;
      const py = pupil.y;
      const pz = Math.sqrt(Math.max(0, EYE_R * EYE_R - px * px - py * py)) - 0.004;
      mA.makeTranslation(side * e.x + px * es, e.y + py * es * blink, e.z + pz * es);
      vA.set(px, py, pz).normalize();
      qA.setFromUnitVectors(AXIS_Z, vA);
      mR.makeRotationFromQuaternion(qA);
      const ps = e.pupil * es;
      mS.makeScale(ps, ps * blink, ps);
      mB2.multiplyMatrices(this.bodyM, mA).multiply(mR).multiply(mS);
      this.skin.set(i === 0 ? P.PUPIL_L : P.PUPIL_R, mB2);
    }
  }

  private poseFeet(b: BeanView): void {
    if (b.ghost) {
      this.skin.hide(P.FOOT_L);
      this.skin.hide(P.FOOT_R);
      return;
    }
    const st = b.state;
    const attached = this.airborne || st === BS.GRABBED || st === BS.FALLING || st === BS.LEDGE || st === BS.CLIMB || Math.abs(this.tumble) > 0.2;
    const speed = len2(b.vx, b.vy);
    const walking = !this.airborne && speed > 0.3 && st !== BS.SLIP && st !== BS.TUMBLE && st !== BS.SPUN;
    const stride = walking ? clamp(speed / 6.5, 0.35, 1) : 0;
    for (let i = 0; i < 2; i++) {
      const side = i === 0 ? -1 : 1;
      const ph = this.walkPhase + (i === 0 ? 0 : Math.PI);
      let fx = side * FOOT.x;
      let fy = FOOT.y;
      let fz = FOOT.z;
      let pitch = 0;
      if (walking) {
        // §12.4 walk cycle: phase from distance travelled (step 0.45 m), lift 0.06.
        fz += 0.13 * stride * Math.sin(ph);
        const lift = Math.max(0, Math.cos(ph));
        fy += FOOT_LIFT * lift * (b.big ? 1.3 : 1);
        pitch = -0.35 * lift * stride;
      } else if (this.airborne || st === BS.FALLING) {
        fy -= 0.02;
        fx += side * 0.05;
        fz -= 0.03 + 0.04 * Math.sin(this.time * 9 + i * 2);
        pitch = 0.3;
      } else if (st === BS.TEETER) {
        fz += 0.04 * Math.sin(this.time * 2 * Math.PI * TEETER_HZ + i * Math.PI);
      }
      mA.makeTranslation(fx, fy, fz);
      mR.makeRotationX(pitch);
      if (attached) mB2.multiplyMatrices(this.bodyM, mA).multiply(mR);
      else mB2.multiplyMatrices(mA, mR);
      this.skin.set(i === 0 ? P.FOOT_L : P.FOOT_R, mB2);
    }
  }

  private poseArms(b: BeanView, dt: number): void {
    const st = b.state;
    const t = this.time;
    const speed = len2(b.vx, b.vy);
    const emoting = this.emoting(b);
    const potato = b.hasPotato && !b.ghost;
    const para = st === BS.RESPAWN && b.z > TUNE.bean.airborneZ;
    for (let i = 0; i < 2; i++) {
      const side = i === 0 ? -1 : 1;
      const chain = i === 0 ? this.armL : this.armR;
      // §12.4 shoulder anchor (±0.40, 0.55, 0) on the body: it follows squash, lean and tumble.
      vA.set(side * SHOULDER.x * 0.97, SHOULDER.y, SHOULDER.z).applyMatrix4(this.bodyM);
      chain.setPoint(0, vA.x, vA.y, vA.z);
      const sx = vA.x;
      const sy = vA.y;
      const sz = vA.z;
      // Hand target relative to the shoulder (root-local) and the pull strength.
      let hx = side * 0.14;
      let hy = -0.4;
      let hz = 0.06;
      let pull = 70;
      let bow = 0.05;
      if (b.ghost) {
        hx = side * 0.22;
        hy = -0.25 + 0.08 * Math.sin(t * 2.2 + i);
        hz = 0.22;
        pull = 40;
      } else if (st === BS.TEETER) {
        // §12.4 windmill: points driven around a circle at 4 Hz.
        const a = t * 2 * Math.PI * TEETER_HZ + (i === 0 ? 0 : 0.6);
        hx = side * 0.2;
        hy = 0.08 + 0.3 * Math.sin(a);
        hz = 0.3 * Math.cos(a);
        pull = 900;
        bow = 0.02;
      } else if (st === BS.FALLING) {
        // §11.2 fall: arms up.
        hx = side * (0.18 + 0.06 * Math.sin(t * 11 + i));
        hy = 0.42;
        hz = 0.05 * Math.sin(t * 9 + i * 2);
        pull = 260;
      } else if (st === BS.LEDGE || st === BS.CLIMB) {
        // Only fingers on the rim, either side of the edge point (§11.2).
        this.rimPoint(b, side, vB);
        hx = vB.x - sx;
        hy = vB.y - sy;
        hz = vB.z - sz;
        pull = 1200;
        bow = 0.04;
      } else if (potato || para) {
        // Both hands up: the hot potato overhead, or the umbrella grip.
        hx = -side * 0.12;
        hy = 0.46 + (potato ? 0.02 * Math.sin(t * 20 + i) : 0);
        hz = 0.06;
        pull = 400;
      } else if (b.floating) {
        if (i === 1) {
          hx = -0.05;
          hy = 0.46;
          hz = -0.04;
          pull = 400;
        } else {
          hx = side * 0.2;
          hy = -0.3;
          hz = 0.1 * Math.sin(t * 2);
        }
      } else if (st === BS.GRABBED || (st === BS.TUMBLE && this.airborne) || st === BS.SLIP) {
        // Flail: arms windmill fast (§11.1 carry).
        const a = t * 2 * Math.PI * (st === BS.GRABBED ? 5 : 3.2) * side + i * 1.7;
        hx = side * 0.24;
        hy = 0.12 + 0.28 * Math.sin(a);
        hz = 0.28 * Math.cos(a);
        pull = 600;
      } else if (st === BS.TUMBLE) {
        hx = side * 0.3;
        hy = 0.05;
        hz = -0.25;
        pull = 160;
      } else if (st === BS.LUNGE) {
        // The shove: both arms thrust forward.
        hx = -side * 0.12;
        hy = 0.06;
        hz = 0.48;
        pull = 1400;
        bow = 0.01;
      } else if (st === BS.GRAB_START) {
        hx = side * 0.05;
        hy = 0.08;
        hz = 0.47;
        pull = 900;
      } else if (st === BS.GRAB_MISS) {
        hx = side * 0.1;
        hy = -0.15;
        hz = 0.42 + 0.04 * Math.sin(t * 20);
        pull = 300;
      } else if (st === BS.CARRYING) {
        // §11.1 carry: hands up under the held victim.
        hx = -side * 0.1;
        hy = 0.3;
        hz = 0.36;
        pull = 900;
      } else if (st === BS.YANKED) {
        hx = -side * 0.08;
        hy = 0.1;
        hz = 0.46;
        pull = 900;
      } else if (st === BS.SPUN) {
        hx = side * 0.45;
        hy = 0.1;
        hz = 0;
        pull = 500;
      } else if (st === BS.PUNCH_WINDUP || st === BS.PUNCH) {
        if (i === 1) {
          // The punching arm rides the spring's base.
          hx = -0.24;
          hy = 0.02;
          hz = 0.36;
          pull = 1000;
        } else {
          hx = 0.06;
          hy = 0.12;
          hz = 0.32;
          pull = 500;
        }
      } else if (emoting) {
        if (this.emote === 1 && i === 1) {                              // wave
          hx = 0.12 + 0.14 * Math.sin(t * 2 * Math.PI * 2.4);
          hy = 0.44;
          hz = 0.08;
          pull = 700;
        } else if (this.emote === 2) {                                  // dance
          const a = t * 2 * Math.PI * 1.1 + i * Math.PI;
          hx = side * 0.22;
          hy = 0.15 + 0.28 * Math.sin(a);
          hz = 0.15;
          pull = 500;
        } else if (this.emote === 3) {                                  // laugh: hold the belly
          hx = -side * 0.22;
          hy = -0.2 + 0.03 * Math.sin(t * 40);
          hz = 0.32;
          pull = 500;
        } else if (this.emote === 4) {                                  // flex
          hx = side * 0.3;
          hy = 0.3;
          hz = 0.02;
          pull = 800;
          bow = -0.14;
        }
      } else if (this.airborne) {
        hx = side * 0.3;
        hy = 0.12;
        hz = 0;
        pull = 160;
      } else if (speed > 0.6) {
        // Run swing, opposite to the feet.
        const swing = Math.sin(this.walkPhase + (i === 0 ? Math.PI : 0));
        hx = side * 0.12;
        hy = -0.36;
        hz = 0.26 * swing * clamp(speed / 6.5, 0.3, 1);
        pull = 240;
      } else {
        // Idle sway.
        hz += 0.03 * Math.sin(t * 1.4 + i * 1.3);
        hx += side * 0.02 * Math.sin(t * 1.1 + i);
      }
      const len = len3(hx, hy, hz);
      const reach = ARM.seg * (ARM.points - 1) * 0.96;
      if (len > reach) {
        hx *= reach / len;
        hy *= reach / len;
        hz *= reach / len;
      }
      const n = ARM.points - 1;
      for (let p = 1; p <= n; p++) {
        const f = p / n;
        const bowK = Math.sin(f * Math.PI) * bow;
        chain.setTarget(p, sx + hx * f + side * bowK, sy + hy * f + bowK * 0.4, sz + hz * f, pull * (0.4 + 0.6 * f));
      }
      chain.step(dt, clamp(this.acc.x, -40, 40), clamp(this.acc.y, -40, 40), clamp(this.acc.z, -40, 40));
      chain.poseTube(this.skin, i === 0 ? BONE_ARM_L : BONE_ARM_R, CHAIN_RINGS, 0.055, 0.05);
      if (this.time < this.handsFlatUntil) {
        mS.makeScale(1.55, 0.35, 1.55);
        mS.setPosition(chain.endX(), chain.endY() - 0.03, chain.endZ());
        this.skin.set(i === 0 ? P.HAND_L : P.HAND_R, mS);
      } else this.skin.setTS(i === 0 ? P.HAND_L : P.HAND_R, chain.endX(), chain.endY(), chain.endZ(), 1);
    }
  }

  /** A rim point (root-local) for ledge fingers: the edge point ± 0.22 m along the rim, at z = 0. */
  private rimPoint(b: BeanView, side: number, out: Vector3): void {
    const tx = -b.edgeNy;
    const ty = b.edgeNx;
    const wx = b.edgeX + tx * 0.22 * side - b.x;
    const wy = b.edgeY + ty * 0.22 * side - b.y;
    const yaw = yawForFacing(this.facing) + this.spinYaw;
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    const k = 1 / Math.max(0.2, this.root.scale.x);
    out.set((wx * c - wy * s) * k, (0.03 - b.z) * k, (wx * s + wy * c) * k);
  }

  private poseDecor(b: BeanView): void {
    const t = this.time;
    const u = this.pm.u;
    const st = b.state;
    const sk = this.skin;
    const ghost = b.ghost;
    u.ghost.value = ghost ? 1 : 0;
    this.aura.visible = ghost;
    this.coreHull.visible = !ghost;
    this.core.castShadow = !ghost;

    // Identity ring (§12.4), hidden for ghosts and while hanging below the rim.
    const ringOn = !ghost && st !== BS.LEDGE && st !== BS.CLIMB;
    if (ringOn && this.isLocal) {
      sk.setTS(P.LOCAL_RING, 0, RING.lift, 0, 1 + 0.06 * Math.sin(t * 2 * Math.PI * PULSE_HZ));
      sk.hide(P.RING);
    } else if (ringOn) {
      sk.setTS(P.RING, 0, RING.lift, 0, 1);
      sk.hide(P.LOCAL_RING);
    } else {
      sk.hide(P.RING);
      sk.hide(P.LOCAL_RING);
    }
    if (ringOn && (this.team === 1 || this.team === 2)) sk.setTS(P.TEAM_RING, 0, RING.lift, 0, 1);
    else sk.hide(P.TEAM_RING);

    // Glyph badge on the ground at the viewer's side of the ring, kept upright on screen.
    const yaw = yawForFacing(this.facing) + this.spinYaw;
    if (ringOn) {
      mR.makeRotationY(-yaw);
      mR.setPosition(GLYPH_BADGE.ahead * Math.sin(-yaw), RING.lift + 0.006, GLYPH_BADGE.ahead * Math.cos(-yaw));
      sk.set(P.GLYPH, mR);
    } else sk.hide(P.GLYPH);

    // Local chevron, 0.5 m above the head, bobbing, facing the camera.
    if (this.isLocal) {
      mR.makeRotationY(-yaw);
      mR.multiply(mC.makeRotationX(-0.55));
      // Stack above whatever the bean wears on its head: hat, paper crown, Crown Grab crown.
      const crowns = (b.holdsCrown ? 0.42 : 0) + (b.crowned ? 0.24 : 0) + (b.hasPotato ? 0.55 : 0);
      mR.setPosition(0, Math.max(this.headTop.y, BODY_HEIGHT) + crowns + CHEVRON_ABOVE + 0.07 * Math.sin(t * 2 * Math.PI * 1.5), 0);
      sk.set(P.CHEVRON, mR);
    } else sk.hide(P.CHEVRON);

    // Teeter: a red pulsing edge glow under the bean (§11.2).
    if (st === BS.TEETER) {
      u.danger.value = 0.5 + 0.5 * Math.sin(t * 2 * Math.PI * TEETER_HZ);
      sk.setTS(P.DANGER, 0, RING.lift * 0.5, 0, 1 + 0.05 * u.danger.value);
    } else sk.hide(P.DANGER);
    // Ledge: the subtle red stomp-target ring around the hanging hands (§11.2), on the rim.
    if (st === BS.LEDGE) {
      this.rimPoint(b, 0, vB);
      u.danger.value = 0.35 + 0.25 * Math.sin(t * 2 * Math.PI * 2);
      sk.setTS(P.STOMP, vB.x, vB.y - 0.01, vB.z, 1 / Math.max(0.2, this.root.scale.x));
    } else sk.hide(P.STOMP);

    // Dizzy stars orbit the head (§2.7, §11.1: 2 / 3 / 4 at 40 / 70 / 100).
    const stars = ghost ? 0 : dizzyStars(b.dizzy);
    for (let i = 0; i < 4; i++) {
      if (i >= stars) {
        sk.hide(P.STAR0 + i);
        continue;
      }
      const a = t * STAR_ORBIT.speed + (i * Math.PI * 2) / stars;
      const pop = 0.9 + 0.15 * Math.sin(t * 8 + i);
      // Orbit around the head; each star faces the camera (world +z) with a little wobble.
      vA.set(Math.cos(a) * STAR_ORBIT.r, STAR_ORBIT.y + 0.06 * Math.sin(a * 2), Math.sin(a) * STAR_ORBIT.r).applyMatrix4(this.headM);
      mR.makeRotationY(-yaw + 0.5 * Math.sin(t * 3 + i));
      mR.multiply(mC.makeRotationZ(t * 2 + i));
      mS.makeScale(pop, pop, pop);
      mR.multiply(mS);
      mR.setPosition(vA.x, vA.y, vA.z);
      sk.set(P.STAR0 + i, mR);
    }
    // Sweat drops at ≥ 70 Dizzy (§2.7): flicked off the head every 0.7 s.
    if (!ghost && b.dizzy >= TUNE.dizzy.flushAt) {
      for (let i = 0; i < 2; i++) {
        const side = i === 0 ? -1 : 1;
        const k = (((t / 0.7 + i * 0.5) % 1) + 1) % 1;
        const sc = k < 0.15 ? k / 0.15 : 1 - smooth01(0.7, 1, k);
        mA.makeTranslation(side * (0.28 + 0.22 * k), -0.12 + 0.12 * Math.sin(k * Math.PI) - 0.25 * k * k, 0.12);
        mR.makeRotationZ(-side * (0.6 + k));
        mS.makeScale(sc, sc, sc);
        mB2.multiplyMatrices(this.headM, mA).multiply(mR).multiply(mS);
        sk.set(i === 0 ? P.SWEAT_L : P.SWEAT_R, mB2);
      }
    } else {
      sk.hide(P.SWEAT_L);
      sk.hide(P.SWEAT_R);
    }

    // Ghost: a wisp tail instead of feet, and the pebble-ready rock floating beneath (§12.4).
    if (ghost) {
      mA.makeTranslation(0, 0.18, 0);
      mR.makeRotationZ(0.15 * Math.sin(t * 2.3));
      mB2.multiplyMatrices(this.bodyM, mA).multiply(mR);
      sk.set(P.TAIL, mB2);
      if (this.time >= this.pebbleReadyAt) {
        mR.makeRotationY(t * 1.2);
        mR.multiply(mC.makeRotationX(0.4));
        mR.setPosition(0, -0.62 + 0.05 * Math.sin(t * 2.2), 0.05);
        sk.set(P.PEBBLE, mR);
      } else sk.hide(P.PEBBLE);
    } else {
      sk.hide(P.TAIL);
      sk.hide(P.PEBBLE);
    }
  }

  private poseAttachments(b: BeanView, view: FrameView, dt: number): void {
    const t = this.time;
    const st = b.state;
    const sk = this.skin;
    const u = this.pm.u;
    const top = this.headTop.y;

    // Hot potato (§4.2): a googly-eyed bomb held overhead, a red pulse at the intensity's rate.
    const potato = b.hasPotato && !b.ghost;
    this.show('potato', potato);
    if (potato) {
      const intensity = view.mode.potatoSlot === b.slot ? clamp(view.mode.potatoIntensity / TUNE.potato.intensityMax, 0, 1) : 0.3;
      const beat = Math.pow(0.5 + 0.5 * Math.sin(t * 2 * Math.PI * (1.2 + intensity * 5)), 3);
      u.potato.value = beat * (0.35 + 0.65 * intensity);
      const sc = 1.15 * (1 + 0.08 * beat);
      mR.makeRotationZ(0.15 * Math.sin(t * 9));
      mS.makeScale(sc, sc, sc);
      mR.multiply(mS);
      mR.setPosition(0, top + 0.28 + 0.03 * Math.sin(t * 7), 0.04);
      sk.set(P.POTATO, mR);
      u.spark.value = 0.5 + 0.5 * Math.sin(t * 61) * Math.sin(t * 37);
      const sp = 0.8 + 0.5 * u.spark.value;
      mA.makeTranslation(0.11, 0.35, 0.03);
      mC.makeRotationZ(t * 9);
      mS.makeScale(sp, sp, 1);
      mB2.multiplyMatrices(mR, mA).multiply(mC).multiply(mS);
      sk.set(P.POTATO_SPARK, mB2);
    }

    // Balloon (§3.3, §11.3): inflates on a scale spring in the identity color; string from the hand.
    const floating = b.floating && !b.ghost;
    this.show('balloon', floating);
    if (floating) {
      this.inflate.target = 1;
      const inf = Math.max(0.05, this.inflate.step(dt));
      const kx = 0.18 + 0.06 * Math.sin(t * 1.3);
      const ky = top + 0.85 + 0.05 * Math.sin(t * 1.9);
      const kz = -0.12 + 0.05 * Math.sin(t * 1.1);
      mR.makeRotationZ(-0.12 + 0.08 * Math.sin(t * 1.7));
      mS.makeScale(inf * 1.25, inf * 1.25, inf * 1.25);
      mR.multiply(mS);
      mR.setPosition(kx, ky, kz);
      sk.set(P.BALLOON, mR);
      const s = this.string;
      s.pinFirst = true;
      s.pinLast = true;
      s.setPoint(0, this.armR.endX(), this.armR.endY(), this.armR.endZ());
      s.setPoint(s.n - 1, kx, ky, kz);
      s.step(dt, clamp(this.acc.x, -30, 30), 0, clamp(this.acc.z, -30, 30));
      s.poseTube(sk, BONE_STRING, CHAIN_RINGS, 0.012, 0.012);
    }

    // Parachute umbrella while respawning from above (§4.3).
    const para = st === BS.RESPAWN && b.z > TUNE.bean.airborneZ && !b.ghost;
    this.show('parachute', para);
    if (para) {
      mR.makeRotationZ(0.14 * Math.sin(t * 1.6));
      mR.multiply(mC.makeRotationX(0.08 * Math.sin(t * 1.1)));
      mR.multiply(mC.makeRotationY(t * 0.8));
      mR.setPosition(0, Math.max(1.5, top + 0.3), 0.04);
      sk.set(P.PARACHUTE, mR);
    }

    // Crowns: the Royale leader's gold paper crown (§4.1) and the Crown Grab crown (§4.3).
    const paper = b.crowned && !b.ghost;
    const royal = b.holdsCrown && !b.ghost;
    this.show('crowns', paper || royal);
    const hatTop = Math.max(0.04, this.hat.top) - 0.03;
    // Both crowns are objectives other players hunt: they read big at gameplay distance.
    if (paper) {
      mA.makeTranslation(0, hatTop, 0);
      mR.makeRotationZ(0.14);
      mS.makeScale(1.25, 1.25, 1.25);
      mB2.multiplyMatrices(this.headM, mA).multiply(mR).multiply(mS);
      sk.set(P.PAPER_CROWN, mB2);
    } else sk.hide(P.PAPER_CROWN);
    if (royal) {
      mA.makeTranslation(0, hatTop + (paper ? 0.22 : 0) + 0.025 * Math.sin(t * 3), 0);
      mR.makeRotationY(t * 0.5);
      mS.makeScale(1.5, 1.5, 1.5);
      mB2.multiplyMatrices(this.headM, mA).multiply(mR).multiply(mS);
      sk.set(P.CROWN, mB2);
    } else sk.hide(P.CROWN);

    // Boxing glove: the coiled spring on the windup, the fist flies out on the punch (§3.3, §11.3).
    const windup = st === BS.PUNCH_WINDUP;
    const punch = st === BS.PUNCH;
    this.show('glove', (windup || punch) && !b.ghost);
    if (windup || punch) {
      let ext: number;
      let shake = 0;
      if (windup) {
        ext = 0.14;
        shake = 0.012 * Math.sin(t * 70);
      } else {
        const out = 1 - Math.pow(1 - clamp(b.st / TUNE.glove.activeTicks, 0, 1), 3);
        const back = clamp((b.st - TUNE.glove.activeTicks) / (TUNE.glove.punchTicks - TUNE.glove.activeTicks), 0, 1);
        ext = Math.max(0.14, (GLOVE_REACH - 0.25) * out * (1 - back * back));
      }
      const baseY = 0.6 + shake;
      const baseZ = 0.4;
      mA.makeTranslation(0, baseY, baseZ);
      mS.makeScale(1, 1, ext);
      mB2.multiplyMatrices(mA, mS);
      sk.set(P.SPRING, mB2);
      const big = punch ? 1.35 : 1.1;
      mA.makeTranslation(shake, baseY, baseZ + ext);
      mS.makeScale(big, big, big);
      mB2.multiplyMatrices(mA, mS);
      sk.set(P.GLOVE, mB2);
    }
  }

  private show(id: AttachmentId, on: boolean): void {
    const a = this.attach[id];
    a.mesh.visible = on;
    a.hull.visible = on;
  }

  headWorldPos(out: Vector3): Vector3 {
    this.root.updateMatrix();
    out.copy(this.headTop).applyMatrix4(this.root.matrix);
    if (this.root.parent) out.applyMatrix4(this.root.parent.matrixWorld);
    return out;
  }

  dispose(): void {
    this.root.removeFromParent();
    this.pm.dispose();
    this.skin.skeleton.dispose();
  }

  /** Test and debug-page introspection (not part of the §24.2 contract). */
  get debug(): BeanRigDebug {
    const d = this.debugState;
    d.expr = this.exprCur;
    d.hand[0].set(this.armL.endX(), this.armL.endY(), this.armL.endZ());
    d.hand[1].set(this.armR.endX(), this.armR.endY(), this.armR.endZ());
    d.flash = this.pm.u.flash.value;
    let n = 0;
    for (const id of ATTACHMENTS) {
      d.attachments[id] = this.attach[id].mesh.visible;
      if (d.attachments[id]) n += 2;
    }
    d.visibleMeshes = this.root.visible ? n + 2 : 0;
    return d;
  }
}

/**
 * The body matrix: B = T(0, bob + pivot) · R(tilt) · R(spin) · T(0, −pivot) · S, where S is the
 * volume-preserving squash (1/√s, s, 1/√s) combined with a stretch q along the horizontal
 * direction (dx, dz) (q along it, 1/√q across), times a uniform puff. Spins pivot at mid-body,
 * leans at the feet.
 */
function composeBody(out: Matrix4, bob: number, tiltX: number, tiltZ: number, spin: number, spAx: number, spAz: number, s: number, q: number, dx: number, dz: number, puff: number): void {
  const ls = 1 / Math.sqrt(s);
  const lq = 1 / Math.sqrt(q);
  const ld = q * ls * puff;
  const ly = s * lq * puff;
  const lp = ls * lq * puff;
  const dl = len2(dx, dz) || 1;
  const ux = dx / dl;
  const uz = dz / dl;
  const pivot = Math.min(1, Math.abs(spin) / 0.6) * 0.55;
  mS.set(
    ld * ux * ux + lp * uz * uz, 0, (ld - lp) * ux * uz, 0,
    0, ly, 0, -pivot,
    (ld - lp) * ux * uz, 0, ld * uz * uz + lp * ux * ux, 0,
    0, 0, 0, 1,
  );
  const tl = len2(tiltX, tiltZ);
  if (tl > 1e-5) qA.setFromAxisAngle(vB.set(tiltX / tl, 0, tiltZ / tl), tl);
  else qA.identity();
  if (Math.abs(spin) > 1e-5) qB.setFromAxisAngle(vB.set(spAx, 0, spAz).normalize(), spin);
  else qB.identity();
  qA.multiply(qB);
  mR.makeRotationFromQuaternion(qA);
  out.multiplyMatrices(mR, mS);
  out.elements[13] += bob + pivot;
}

export function createBeanRig(lib: MaterialLibrary): BeanRig {
  return new BeanRigImpl(lib);
}
